import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { LoginPage, RegisterPage, LandingPage, PreviewPage, MainPage } from './pages';
import MethodologyPage from './pages/methodology/MethodologyPage';
import AboutPage from './pages/about/AboutPage';
import ContactPage from './pages/contact/ContactPage';
import InsightsPage from './pages/insights/InsightsPage';
import ConversionNotCompute from './pages/insights/ConversionNotCompute';
import CapitalFormationBottleneck from './pages/insights/CapitalFormationBottleneck';
import ReadingNationalAiReadiness from './pages/insights/ReadingNationalAiReadiness';
import ConveningsPage from './pages/convenings/ConveningsPage';
import HouseOfLords20260415 from './pages/convenings/HouseOfLords20260415';
import HouseOfLords20260702 from './pages/convenings/HouseOfLords20260702';
import SAPILogin from './pages/Login_RequestAccess';
import SAPILoginRequestAccess from './pages/SAPI_Login_RequestAccess';
import QRLoginEmailPage from './pages/QRLoginEmailPage';
import QRLoginOtpPage from './pages/QRLoginOtpPage';
import QRVerifiedPage from './pages/QRVerifiedPage';
import SAPIBriefing from './components/SAPI_P3_Briefing';
import SAPIDimIntro from './components/SAPI_P4_DimIntro';
import SAPIQuiz from './components/SAPI_P5_Quiz';
import SAPICalculating from './components/SAPI_P6_Calculating';
import SAPIResults from './components/SAPI_P7_Results';
import P7Results from './pages/common/result';
import SAPIScorecard from './components/SAPI_P8_Scorecard';
import SAPIPeerComparison from './components/SAPI_P9_PeerComparison';
import SAPIRoadmap from './components/SAPI_P10_Roadmap';
import SAPIArcDashboard from './pages/PowerArc';
import TheIndexPage from './pages/index/TheIndexPage';
import SAPIIndexPageDemo from './pages/SAPI_IndexPage_Enhancements';
import AdminDashboard from './components/AdminDashboard';
import SubmissionsList from './pages/admin/SubmissionsList';
import SubmissionDetail from './pages/admin/SubmissionDetail';
import QuestionEditor from './components/SAPI_E1_QuestionEditor';
import ProtectedRoute from './components/ProtectedRoute';
import 'flag-icons/css/flag-icons.min.css';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Wrapper component to handle briefing navigation - resets dimension to 0
function BriefingWrapper({ setCurrentDimension }) {
  const navigate = useNavigate();
  
  const handleBegin = () => {
    setCurrentDimension(0); // Reset to dimension 1 when starting
    localStorage.removeItem('sapi_answers'); // Clear previous answers
    navigate('/dimintro');
  };
  
  return <SAPIBriefing onBegin={handleBegin} />;
}

// Wrapper component to handle dimension navigation
function DimIntroWrapper({ currentDimension, setCurrentDimension }) {
  const navigate = useNavigate();
  
  return (
    <SAPIDimIntro 
      currentIndex={currentDimension}
      onBegin={() => navigate('/quiz')}
      onBack={() => {
        if (currentDimension === 0) {
          navigate('/briefing');
        } else {
          setCurrentDimension(currentDimension - 1);
        }
      }}
    />
  );
}

// Wrapper component for P7Results to provide required props
function ResultsWrapper() {
  const [appState, setAppState] = useState({
    scores: {},
    compositeScore: null,
    tier: null,
    orgProfile: {},
    email: '',
    emailCaptured: false
  });

  return (
    <P7Results 
      appState={appState}
      setAppState={setAppState}
      setCurrentPage={(page) => {
        // Handle navigation from results page
        if (page === 'scorecard') {
          window.location.href = '/scorecard';
        } else if (page === 'roadmap') {
          window.location.href = '/roadmap';
        } else if (page === 'upgrade') {
          window.location.href = '/peercomparison';
        }
      }}
    />
  );
}

// Wrapper component to handle quiz navigation
function QuizWrapper({ currentDimension, setCurrentDimension }) {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [assessmentResults, setAssessmentResults] = useState(null);
  
  const submitToApi = async (allAnswers) => {
    try {
      const { submitAssessment } = await import('./services/assessmentService');
      
      // Get user profile from localStorage (stored during preview step)
      const userProfile = JSON.parse(localStorage.getItem('sapi_profile') || '{}');
      const profileId = userProfile.profile_id || userProfile.id;
      
      if (!profileId) {
        console.error('No profile ID found in localStorage');
        return null;
      }
      
      const answerArray = Object.entries(allAnswers).map(([questionId, data]) => ({
        question_id: parseInt(questionId.replace('Q', '')),
        selected_option: data.selectedOption
      }));
      
      const response = await submitAssessment(profileId, answerArray);
      if (response.success) {
        setAssessmentResults(response.data);
        // Store assessment ID for later retrieval
        if (response.data.assessment_id) {
          localStorage.setItem('sapi_assessment_id', response.data.assessment_id);
        }
        return response.data;
      }
    } catch (error) {
      console.error('Assessment submission failed:', error);
      // Fallback: calculate locally
      return null;
    }
  };
  
  return (
    <SAPIQuiz 
      appState={{ currentDimension, answers }}
      setAppState={(newState) => {
        // Merge new answers with existing ones using functional update
        if (newState.answers) {
          setAnswers(prevAnswers => ({
            ...prevAnswers,
            ...newState.answers
          }));
        }
      }}
      setCurrentPage={async (page, finalAnswers) => {
        if (page === 'dimIntro') {
          setCurrentDimension(currentDimension + 1);
          navigate('/dimintro');
        } else if (page === 'calculating') {
          // Submit assessment before navigating
          const allAnswers = finalAnswers || answers;
          await submitToApi(allAnswers);
          navigate('/calculating', { state: { answers: allAnswers } });
        }
      }}
    />
  );
}

function App() {
  const [currentDimension, setCurrentDimension] = useState(0);

  return (
    <Router>
      <div className="App">
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Navigate to="/main" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sapi-login" element={<SAPILogin />} />
          <Route path="/qr-login" element={<QRLoginEmailPage />} />
          <Route path="/qr-login/verify" element={<QRLoginOtpPage />} />
          <Route path="/qr-login/success" element={<QRVerifiedPage />} />
          <Route path="/sapi-login-request" element={<SAPILoginRequestAccess />} />
          <Route path="/home" element={<ProtectedRoute allowedRole={2}><LandingPage /></ProtectedRoute>} />
          <Route path="/preview" element={<ProtectedRoute allowedRole={2}><PreviewPage /></ProtectedRoute>} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/briefing" element={<ProtectedRoute allowedRole={2}><BriefingWrapper setCurrentDimension={setCurrentDimension} /></ProtectedRoute>} />
          <Route 
            path="/dimintro" 
            element={<ProtectedRoute allowedRole={2}><DimIntroWrapper 
              currentDimension={currentDimension} 
              setCurrentDimension={setCurrentDimension}
            /></ProtectedRoute>} 
          />
          <Route 
            path="/quiz" 
            element={<ProtectedRoute allowedRole={2}><QuizWrapper 
              currentDimension={currentDimension}
              setCurrentDimension={setCurrentDimension}
            /></ProtectedRoute>} 
          />
          <Route path="/calculating" element={<ProtectedRoute allowedRole={2}><SAPICalculating /></ProtectedRoute>} />
          <Route path="/results" element={<ProtectedRoute allowedRole={2}><SAPIResults /></ProtectedRoute>} />
          <Route path="/results-report" element={<ProtectedRoute allowedRole={2}><ResultsWrapper /></ProtectedRoute>} />
          <Route path="/scorecard" element={<ProtectedRoute allowedRole={2}><SAPIScorecard /></ProtectedRoute>} />
          <Route path="/peercomparison" element={<ProtectedRoute allowedRole={2}><SAPIPeerComparison /></ProtectedRoute>} />
          <Route path="/roadmap" element={<ProtectedRoute allowedRole={2}><SAPIRoadmap /></ProtectedRoute>} />
          <Route path="/powerarc" element={<SAPIArcDashboard />} />
          <Route path="/sapi-index" element={<TheIndexPage />} />
          <Route path="/the-index" element={<Navigate to="/sapi-index" replace />} />
          <Route path="/sapi-index-demo" element={<SAPIIndexPageDemo />} />
          <Route path="/main" element={<MainPage />} />
          <Route path="/methodology" element={<MethodologyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/insights/conversion-not-compute" element={<ConversionNotCompute />} />
          <Route path="/insights/capital-formation-bottleneck" element={<CapitalFormationBottleneck />} />
          <Route path="/insights/reading-national-ai-readiness" element={<ReadingNationalAiReadiness />} />
          <Route path="/convenings" element={<ConveningsPage />} />
          <Route path="/convenings/2026-04-15-house-of-lords" element={<HouseOfLords20260415 />} />
          <Route path="/convenings/2026-07-02-house-of-lords" element={<HouseOfLords20260702 />} />
          <Route path="/admin" element={<ProtectedRoute allowedRole={1}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admindashboard" element={<ProtectedRoute allowedRole={1}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute allowedRole={1}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/submissionlist" element={<ProtectedRoute allowedRole={1}><SubmissionsList /></ProtectedRoute>} />
          <Route path="/submissiondetail" element={<ProtectedRoute allowedRole={1}><SubmissionDetail /></ProtectedRoute>} />
          <Route path="/leadspipeline" element={<ProtectedRoute allowedRole={1}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/leaddetail" element={<Navigate to="/admin" replace />} />
          <Route path="/leaddetailcombined" element={<Navigate to="/admin" replace />} />
          <Route path="/questioneditor" element={<QuestionEditor />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </Router>
  );
}
export default App;
